import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import getSafeImageSrc from 'editor/src/util/getSafeImageSrc';

import DesktopZoomControls from './DesktopZoomControls';
import {
  PREVIEW_DOUBLE_CLICK_SCALE,
  PREVIEW_RESET_VIEW,
  PREVIEW_SCALE_STEP,
  PreviewView,
  Size,
  clampPreviewView,
  zoomPreviewAt,
} from './previewZoomMath';

import styles from './index.module.scss';

interface ZoomableImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  onLoad?: () => void;
  // Mouse zoom and pan: +/- buttons, wheel or trackpad pinch to zoom, drag to move,
  // double-click to toggle. Replaces the touch-only pinch/double-tap handling.
  desktopZoom?: boolean;
  // Return to the fitted view whenever this changes (e.g. the carousel moved on).
  resetKey?: unknown;
}

const WHEEL_ZOOM_FACTOR = 0.995;

const DOUBLE_TAP_THRESHOLD = 300; // ms
const MIN_ZOOM_SCALE = 1;
const MAX_ZOOM_SCALE = 3;
const DOUBLE_TAP_ZOOM_SCALE = 2;
const SMOOTH_ZOOM_DAMPENING = 0.1;

function ZoomableImage({
  src,
  alt,
  className,
  containerClassName,
  onLoad,
  style,
  desktopZoom = false,
  resetKey,
}: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [view, setView] = useState<PreviewView>(PREVIEW_RESET_VIEW);
  const [dragging, setDragging] = useState(false);
  // The pointer at the last drag step: each move applies only its own step, so a wheel
  // zoom part-way through a drag does not make the image jump.
  const lastPointer = useRef<{ x: number; y: number } | undefined>();
  const viewRef = useRef(view);
  viewRef.current = view;
  const lastTapTime = useRef(0);
  const initialDistance = useRef<number>(0);
  const currentScale = useRef<number>(1);
  const isZoomed = useRef<boolean>(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || desktopZoom) {
      return undefined;
    }

    const handleDoubleTap = (e: TouchEvent): void => {
      const currentTime = new Date().getTime();
      const tapLength = currentTime - lastTapTime.current;

      if (tapLength < DOUBLE_TAP_THRESHOLD && tapLength > 0) {
        e.preventDefault();
        const img = containerRef.current?.querySelector('img');
        if (img) {
          isZoomed.current = !isZoomed.current;
          currentScale.current = isZoomed.current ? DOUBLE_TAP_ZOOM_SCALE : MIN_ZOOM_SCALE;

          const rect = img.getBoundingClientRect();
          const offsetX = e.touches[0].clientX - rect.left;
          const offsetY = e.touches[0].clientY - rect.top;
          const percentX = offsetX / rect.width;
          const percentY = offsetY / rect.height;

          img.style.transformOrigin = `${percentX * 100}% ${percentY * 100}%`;
          img.style.transform = `scale(${currentScale.current})`;
          img.classList.toggle(styles.zoomed);

          if (isZoomed.current) {
            const scrollLeft = (img.offsetWidth * currentScale.current - container.clientWidth) * percentX;
            const scrollTop = (img.offsetHeight * currentScale.current - container.clientHeight) * percentY;

            container.scrollTo({
              top: scrollTop,
              left: scrollLeft,
              behavior: 'smooth',
            });
          } else {
            container.scrollTo({ top: 0, left: 0 });
          }
        }
      }
      lastTapTime.current = currentTime;
    };

    const handleTouchStart = (e: TouchEvent): void => {
      if (e.touches.length === 2) {
        e.preventDefault();
        initialDistance.current = Math.hypot(
          e.touches[0].pageX - e.touches[1].pageX,
          e.touches[0].pageY - e.touches[1].pageY,
        );
      }
    };

    const handleTouchMove = (e: TouchEvent): void => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const distance = Math.hypot(e.touches[0].pageX - e.touches[1].pageX, e.touches[0].pageY - e.touches[1].pageY);

        const scaleDiff = (distance / initialDistance.current - 1) * SMOOTH_ZOOM_DAMPENING;
        const newScale = Math.min(Math.max(currentScale.current + scaleDiff, MIN_ZOOM_SCALE), MAX_ZOOM_SCALE);

        const img = containerRef.current?.querySelector('img');
        if (img) {
          img.style.transition = 'transform 0.2s ease-out';
          img.style.transform = `scale(${newScale})`;
          currentScale.current = newScale;
          isZoomed.current = newScale > MIN_ZOOM_SCALE;
          img.classList.toggle(styles.zoomed, isZoomed.current);
        }
      }
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchstart', handleDoubleTap);
    container.addEventListener('touchmove', handleTouchMove);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchstart', handleDoubleTap);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [desktopZoom]);

  const measure = useCallback((): { image: Size; container: Size } | undefined => {
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image) {
      return undefined;
    }
    // offsetWidth/Height ignore the transform, so this is the fitted size at scale 1.
    return {
      image: { width: image.offsetWidth, height: image.offsetHeight },
      container: { width: container.clientWidth, height: container.clientHeight },
    };
  }, []);

  const zoomAt = useCallback(
    (nextScale: (scale: number) => number, clientX?: number, clientY?: number) => {
      const sizes = measure();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!sizes || !rect) {
        return;
      }
      const point =
        clientX === undefined || clientY === undefined
          ? { x: 0, y: 0 }
          : { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 };
      setView((current) => zoomPreviewAt(current, nextScale(current.scale), point, sizes.image, sizes.container));
    },
    [measure],
  );

  useEffect(() => {
    setView(PREVIEW_RESET_VIEW);
  }, [resetKey, src]);

  // React registers wheel listeners as passive, so preventDefault needs a native listener.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !desktopZoom) {
      return undefined;
    }
    // As on the canvas: a pinch (reported with ctrlKey) or Ctrl/Cmd + wheel zooms, a plain
    // scroll moves a zoomed image, and a plain scroll over the fitted image does nothing.
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        zoomAt((scale) => scale * WHEEL_ZOOM_FACTOR ** e.deltaY, e.clientX, e.clientY);
        return;
      }
      if (viewRef.current.scale <= 1) {
        return;
      }
      e.preventDefault();
      const sizes = measure();
      if (!sizes) {
        return;
      }
      setView((current) =>
        clampPreviewView(
          { scale: current.scale, x: current.x - e.deltaX, y: current.y - e.deltaY },
          sizes.image,
          sizes.container,
        ),
      );
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [desktopZoom, zoomAt, measure]);

  useEffect(() => {
    if (!dragging) {
      return undefined;
    }
    const onMove = (e: MouseEvent) => {
      // The button was released somewhere we never heard about (another window).
      if (e.buttons === 0) {
        setDragging(false);
        return;
      }
      const last = lastPointer.current;
      const sizes = measure();
      if (!last || !sizes) {
        return;
      }
      const stepX = e.clientX - last.x;
      const stepY = e.clientY - last.y;
      lastPointer.current = { x: e.clientX, y: e.clientY };
      setView((current) =>
        clampPreviewView(
          { scale: current.scale, x: current.x + stepX, y: current.y + stepY },
          sizes.image,
          sizes.container,
        ),
      );
    };
    const onUp = () => setDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    window.addEventListener('blur', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onUp);
    };
  }, [dragging, measure]);

  const onMouseDown = (e: React.MouseEvent) => {
    if (!desktopZoom || view.scale <= 1 || e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    lastPointer.current = { x: e.clientX, y: e.clientY };
    setDragging(true);
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    if (!desktopZoom) {
      return;
    }
    if (view.scale > 1) {
      setView(PREVIEW_RESET_VIEW);
    } else {
      zoomAt(() => PREVIEW_DOUBLE_CLICK_SCALE, e.clientX, e.clientY);
    }
  };

  const safeSrc = useMemo(() => getSafeImageSrc(src), [src]);

  const handleImageLoad = () => {
    onLoad?.();
  };

  if (!desktopZoom) {
    return (
      <div ref={containerRef} className={`${styles.container} ${styles.touchPinchZoom} ${containerClassName || ''}`}>
        <img
          src={safeSrc}
          alt={alt}
          className={`${styles.image} ${className || ''}`}
          style={style}
          onLoad={handleImageLoad}
        />
      </div>
    );
  }

  const zoomed = view.scale > 1;
  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${styles.desktopZoom} ${containerClassName || ''}`}
      data-zoomed={zoomed}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
    >
      <img
        ref={imageRef}
        src={safeSrc}
        alt={alt}
        draggable={false}
        className={`${styles.image} ${zoomed ? styles.grab : ''} ${dragging ? styles.grabbing : ''} ${className || ''}`}
        style={{
          ...style,
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          transition: dragging ? 'none' : undefined,
        }}
        onLoad={handleImageLoad}
      />
      <DesktopZoomControls
        scale={view.scale}
        onZoomOut={() => zoomAt((scale) => scale - PREVIEW_SCALE_STEP)}
        onZoomIn={() => zoomAt((scale) => scale + PREVIEW_SCALE_STEP)}
        onReset={() => setView(PREVIEW_RESET_VIEW)}
      />
    </div>
  );
}

export default ZoomableImage;
